import { useListGraphWebhooks } from '@squareup/dex-feature-hooks-graph';
import {
  EndpointsWebhookCardList,
  ReferenceGuide,
  ReferencePage,
} from '@squareup/dex-tech-ui';
import { ReferenceHrefBuilders, toUrl } from '@squareup/dex-tech-utils-routing';
import { Head } from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Box } from '@squareup/dex-ui-shared-base';
import { getNextPublicSquareDocsURL } from '@squareup/dex-utils-environment';
import {
  addWebhookByApiCategoryToMap,
  getSortedCategoriesWebhooksMap,
} from '@squareup/dex-utils-oas';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import React, { ReactNode, useCallback, useMemo, useRef } from 'react';

import { SquareVersionPage } from '../../../../shared/SquareVersionPage';

const PageContent = () => {
  const title = 'Webhooks';
  const description = 'An index of all the webhooks in the Square APIs';
  const params = useParams();
  const { webhooks } = useListGraphWebhooks();

  const webhookListRef = useRef<HTMLElement | null>(null);

  const [sortedCategoriesWebhooksMap] = useMemo(() => {
    if (!webhooks) {
      return [];
    }

    const categoriesWebhooksMap = webhooks.reduce(
      addWebhookByApiCategoryToMap,
      new Map()
    );

    const sortedCategoriesWebhooksMap = getSortedCategoriesWebhooksMap(
      categoriesWebhooksMap
    );

    return [sortedCategoriesWebhooksMap];
  }, [webhooks]);

  const setWebhookListRef = useCallback((ref: HTMLElement | null) => {
    webhookListRef.current = ref;
  }, []);

  const aside = useMemo(() => {
    return {
      trackingId: 'square-version-webhooks-categories',
      content: sortedCategoriesWebhooksMap ? (
        <EndpointsWebhookCardList
          categoriesWebhooksMap={sortedCategoriesWebhooksMap}
          setListRef={setWebhookListRef}
        />
      ) : null,
    };
  }, [setWebhookListRef, sortedCategoriesWebhooksMap]);

  return (
    <>
      <Head
        description={description}
        preconnectUrl={getNextPublicSquareDocsURL()}
        title={title}
        canonicalPath={toUrl(
          ReferenceHrefBuilders.versionDeprecatedWebhooks({
            square_version: params.square_version as string,
          })
        )}
      ></Head>
      <ReferencePage
        eyebrow="Square API"
        title="Webhook Events"
        subTitle="Get notified about Square API events in realtime"
        description={
          <>
            <Box margin={{ bottom: '2x' }}>
              <Markdown
                markdown={`Square Webhooks are subscriptions to events that update data in
                a Square account. Webhooks POST data state updates on specific
                events. To detect changes to data, typical APIs would require
                calls at regular time intervals. Square Webhooks replace regular
                API calls with instant, real-time notifications.`}
              ></Markdown>
              <ReferenceGuide
                title="Guide"
                content="Webhooks"
                topMargin={true}
                linkProps={{
                  href: 'https://developer.squareup.com/docs/webhooks/overview',
                  trackingId: 'navigate-to-webhooks-guide-help',
                }}
              />
            </Box>
          </>
        }
        aside={aside}
      />
    </>
  );
};

class Page extends SquareVersionPage {
  public static override routeHandlerName = '[square_version]/webhooks';

  protected override getPageContent(): ReactNode {
    return super.getPageContent({
      children: <PageContent />,
    });
  }
}

export default Page;
